// src/App.js - Updated with Collaborator Support
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useUserContext } from "./contexts/UserContext";

// Importar LandingPage, TermsOfService, PrivacyPolicy, AboutUs, TutorialsGuide, ContactPage e BookDemo
import LandingPage from "./components/Landing/LandingPage";
import TermsOfService from "./components/Landing/TermsOfService";
import PrivacyPolicy from "./components/Landing/PrivacyPolicy";
import AboutUs from "./components/Landing/AboutUs";
import TutorialsGuide from "./components/Landing/TutorialsGuide";
import ContactPage from "./components/Landing/ContactPage";
import BookDemo from "./components/Landing/BookDemo";

// Importar o novo componente SimpleNFeViewer
import SimpleNFeViewer from "./components/NFe/SimpleNFeViewer";

// Auth Components
import Login from "./components/Auth/Login";
import SubscriptionRoute from "./components/Auth/SubscriptionRoute";
import SignupSuccess from "./components/Auth/SignupSuccess";
import RecoverSubscription from "./components/Auth/RecoverSubscription";
import ForgotPassword from "./components/Auth/ForgotPassword";
import RenewalSuccess from "./components/Auth/RenewalSuccess"; 
import CategorySelection from "./components/Auth/CategorySelection";
import FirstLoginRedirect from "./components/Auth/FirstLoginRedirect";

// Stripe Subscription Components
import Pricing from "./components/Pricing/Pricing";
import Upgrade from "./components/Pricing/Upgrade";

// Main App Components
import Dashboard from "./components/Dashboard/Dashboard";
import CollaboratorDashboardWrapper from "./components/Dashboard/CollaboratorDashboardWrapper";
import DREReport from "./components/DRE/DREReport";
import CategoryDetails from "./components/Categories/CategoryDetails";
import Transactions from "./components/Transactions/Transactions";
import NonCategorized from "./components/Transactions/NonCategorized";
import EditCategorized from "./components/Transactions/EditCategorized";
import PeriodManager from "./components/Periods/PeriodManager";
import CashEntry from "./components/CashEntry/CashEntry";
import Settings from "./components/Settings/Settings";
import Charts from "./components/Charts/Charts";
import TaxRegimeComparison from "./components/TaxRegimes/TaxRegimeComparison";
import Tutorial from "./components/Tutorial/Tutorial";
import Contact from "./components/Contact/Contact";

// Admin Components
import SubscriptionDebugger from "./components/Admin/SubscriptionDebugger";

// Workspace Management Component
import BusinessProfileManagement from "./components/Workspace/BusinessProfileManagement";
import WorkspacePage from "./components/Workspace/WorkspacePage";

// Invitation Component
import InvitePage from "./components/Invitation/InvitePage";

import "./App.css";

// Rota protegida que requer uma assinatura ativa
const ProtectedSubscriptionRoute = ({ children, requiredPlan = null }) => {
  const { user, loading } = useUserContext();

  if (loading) {
    return <div className="app-loading">Carregando...</div>;
  }

  // Se não houver usuário autenticado, redirecionar para o login
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Verificar a assinatura usando o SubscriptionRoute
  return (
    <SubscriptionRoute requiredPlan={requiredPlan}>
      {children}
    </SubscriptionRoute>
  );
};

// Rota pública que redireciona usuários já autenticados
const PublicRoute = ({ children }) => {
  const { user, loading } = useUserContext();

  if (loading) {
    return <div className="app-loading">Carregando...</div>;
  }

  // Se o usuário já estiver autenticado, redireciona para o dashboard
  if (user) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Rota de admin (protegida)
const AdminRoute = ({ children }) => {
  const { user, loading, hasAccess } = useUserContext();

  if (loading) {
    return <div className="app-loading">Carregando...</div>;
  }

  // Se não houver usuário autenticado, redirecionar para o login
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Verificar se o usuário é admin (aqui poderia ter uma lógica mais elaborada)
  const isAdmin = user.email === "admin@dominio.com" || user.isAdmin === true || hasAccess("business_pro");

  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function App() {
  return (
    // REMOVIDO: <UserProvider> e <DashboardProvider> e <Router> - já estão no index.js
    <Routes>
      {/* Rota para a Landing Page como página inicial */}
      <Route path="/" element={<LandingPage />} />

      {/* Rotas públicas para páginas legais e institucionais */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/tutorials" element={<TutorialsGuide />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/agendar-demonstracao" element={<BookDemo />} />
      <Route path="/demo" element={<BookDemo />} />

      {/* NOVA ROTA: Adicionar a rota para a página de convite */}
      <Route path="/invite" element={<InvitePage />} />

      {/* Rotas públicas */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />

      {/* Rota de preços - acessível por todos */}
      <Route path="/pricing" element={<Pricing />} />

      {/* Rotas de gerenciamento de assinatura */}
      <Route path="/signup-success" element={<SignupSuccess />} />
      <Route path="/recover-subscription" element={<RecoverSubscription />} />
      <Route path="/renewal-success" element={<RenewalSuccess />} />

      {/* Rota de upgrade para usuários autenticados */}
      <Route
        path="/upgrade"
        element={
          <ProtectedSubscriptionRoute>
            <Upgrade />
          </ProtectedSubscriptionRoute>
        }
      />

      {/* Rotas protegidas por assinatura */}
      <Route
        path="/dashboard"
        element={
          <ProtectedSubscriptionRoute>
            <FirstLoginRedirect>
              <CollaboratorDashboardWrapper />
            </FirstLoginRedirect>
          </ProtectedSubscriptionRoute>
        }
      />

      {/* Nova rota para gerenciamento de workspace */}
      <Route
        path="/workspace"
        element={
          <ProtectedSubscriptionRoute>
            <WorkspacePage />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/select-categories"
        element={
          <ProtectedSubscriptionRoute>
            <CategorySelection />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/dre"
        element={
          <ProtectedSubscriptionRoute requiredPlan="business">
            <DREReport />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/charts"
        element={
          <ProtectedSubscriptionRoute>
            <Charts />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/transactions"
        element={
          <ProtectedSubscriptionRoute>
            <Transactions />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/categorias"
        element={
          <ProtectedSubscriptionRoute>
            <CategoryDetails />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/regimes-tributarios"
        element={
          <ProtectedSubscriptionRoute requiredPlan="business">
            <TaxRegimeComparison />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/não-categorizados"
        element={
          <ProtectedSubscriptionRoute>
            <NonCategorized />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/nao-categorizados"
        element={
          <ProtectedSubscriptionRoute>
            <NonCategorized />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/editar-categorizados"
        element={
          <ProtectedSubscriptionRoute>
            <EditCategorized />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/periods"
        element={
          <ProtectedSubscriptionRoute>
            <PeriodManager />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/cash-entry"
        element={
          <ProtectedSubscriptionRoute>
            <CashEntry />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedSubscriptionRoute>
            <Settings />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/como-usar"
        element={
          <ProtectedSubscriptionRoute>
            <Tutorial />
          </ProtectedSubscriptionRoute>
        }
      />

      <Route
        path="/contato"
        element={
          <ProtectedSubscriptionRoute>
            <Contact />
          </ProtectedSubscriptionRoute>
        }
      />

      {/* Nova rota para o visualizador de NFe */}
      <Route
        path="/nfe-viewer"
        element={
          <ProtectedSubscriptionRoute>
            <SimpleNFeViewer />
          </ProtectedSubscriptionRoute>
        }
      />

      {/* Nova rota para o depurador de assinatura */}
      <Route
        path="/subscription-debug"
        element={
          <ProtectedSubscriptionRoute>
            <SubscriptionDebugger />
          </ProtectedSubscriptionRoute>
        }
      />

      {/* Rota de fallback */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    // REMOVIDO: Fechamentos de UserProvider, DashboardProvider e Router
  );
}

export default App;